/* eslint-disable @typescript-eslint/naming-convention */
import { Spacer } from '@aircall/tractor';
import { GridItem, GridLayout } from '@components/GridLayout/GridLayout';
import { PaperForm } from '@components/PaperForm';
import { PictureUploader } from '@components/PictureUploader/PictureUploader';
import { TextField } from '@components/TextField/TextField';
import { UserAvatar } from '@components/UserAvatar/UserAvatar';
import { getInitials, validateRequired } from '@dashboard/library';
import { ProfileImageEntityDomain } from '@generated/globalTypes';
import { UpdateAgentMutation, UpdateAgentMutationVariables } from '@generated/UpdateAgentMutation';
import { UserFullFragment } from '@generated/UserFullFragment';
import { UPDATE_AGENT_MUTATION } from '@graphql/mutations/UpdateAgentMutation';
import { useDeletePicture, useUploadPicture } from '@hooks/usePicture';
import { useTracker } from '@hooks/useTracker/useTracker';
import { useUpdateUserInformation } from '@hooks/useUpdateUserInformation';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { useTranslation } from 'react-i18next';

interface PersonalInformationSectionProps extends UserProfileTabProps {
  isCurrentUser: boolean;
}

export const getUploadVariables =
  (ID: string) =>
  (filePath: string): UpdateAgentMutationVariables => ({
    input: {
      ID,
      pictureURL: filePath,
    },
  });

export function PersonalInformationSection({
  user,
  isCurrentUser,
}: PersonalInformationSectionProps) {
  const { t } = useTranslation();
  const { updateUser } = useUpdateUserInformation(user, isCurrentUser);
  const { track } = useTracker();

  const { firstName, lastName, pictureURL, isActive, ID } = user;
  const initials = getInitials(firstName, lastName);

  const [onUpdate, updateState] = useUploadPicture<
    UpdateAgentMutation,
    UpdateAgentMutationVariables
  >({
    query: UPDATE_AGENT_MUTATION,
    getVariables: getUploadVariables(ID),
  });

  const [onDelete, deleteState] = useDeletePicture<
    UpdateAgentMutation,
    UpdateAgentMutationVariables
  >({
    query: UPDATE_AGENT_MUTATION,
    variables: {
      input: {
        ID,
        pictureURL: '',
      },
    } as UpdateAgentMutationVariables,
  });

  return (
    <Spacer fluid direction='vertical' space='m'>
      <PaperForm<UserFullFragment>
        shouldBlockNavigation
        title={t('user_profile.general.personal_info.title')}
        formProps={{
          onSubmit: updateUser,
          initialValues: user,
        }}
      >
        {() => (
          <GridLayout gridColumnGap={{ _: 'm', md: 'l' }}>
            <GridItem gridRow='1' xs={12} md={6}>
              <PictureUploader
                hasPictureUrl={!!pictureURL}
                avatar={
                  <UserAvatar
                    src={pictureURL}
                    disabled={!isActive}
                    initials={initials}
                    size='xLarge'
                  />
                }
                deleteState={deleteState}
                updateState={updateState}
                onDelete={() => {
                  track({
                    event: 'user_settings_edited',
                    payload: {
                      target_user: isCurrentUser ? 'self' : 'other_user',
                      settings_section: 'General',
                      setting: 'Profile picture',
                      action: 'profile_picture_deleted',
                    },
                  });
                  return onDelete();
                }}
                onUpdate={(e) => {
                  track({
                    event: 'user_settings_edited',
                    payload: {
                      target_user: isCurrentUser ? 'self' : 'other_user',
                      settings_section: 'General',
                      setting: 'Profile picture',
                      action: 'profile_picture_updated',
                    },
                  });
                  return onUpdate(e, {
                    ID,
                    domain: ProfileImageEntityDomain.USERS,
                  });
                }}
              />
            </GridItem>
            <GridItem gridRow={{ _: 2, md: 1 }} xs={12} md={6}>
              <div style={{ marginBottom: '24px' }}>
                <TextField
                  name='firstName'
                  validate={validateRequired}
                  label={t('app.form.first_name.label')}
                  size='regular'
                  data-test='user-profile-input-firstname'
                />
              </div>
              <TextField
                name='lastName'
                validate={validateRequired}
                label={t('app.form.last_name.label')}
                size='regular'
                data-test='user-profile-input-lastname'
              />
            </GridItem>
          </GridLayout>
        )}
      </PaperForm>
    </Spacer>
  );
}
